import { parse } from 'date-fns/parse';
import { format } from 'date-fns/format';
import { HELIOS_SUPPORTED_LANGUAGES } from './constant';
import { Fulfillment } from 'services/userPreferences/types';
import { DATE_FNS_LOCALES } from 'constants/languageConstants';

/**
 * Checks if the environment is in development mode
 */
export const isDev = () => process.env['NODE_ENV'] === 'development';

/**
 *  Checks the production environments
 * @returns boolean
 */
export const isProd = () => {
  const host = process.env['COMMERCE_API_HOST'];
  return host.includes('prod');
};

/**
 * Get the a promise settled value from PromiseSettledResult
 */
export const getPromiseSettledValue = <T>(
  result: PromiseSettledResult<T>
): T | null => {
  if (result.status === 'fulfilled') {
    return result.value;
  }

  return null;
};

/**
 * @param fn async function
 * @param timeoutMS timeout in milliseconds
 * @description
 * Takes an asnc function and returns a similar function with the capability to throw an exception
 * if it's not solved within the specified timeout in milliseconds.
 * If no ms param is passed, it returns the original function
 */
export const withTimeout = async <T>(
  fn: () => Promise<T>,
  timeoutMS?: number
): Promise<T> => {
  if (timeoutMS) {
    return Promise.race([
      fn(),
      new Promise((_, rej) =>
        setTimeout(() => rej(new Error('request timed out')), timeoutMS)
      )
    ]) as Promise<T>;
  }
  return fn();
};

export const getSetCookieValue = (cookies: string[], cookieName: string) => {
  const value = cookies.filter((val) => val.includes(cookieName))?.[0];

  if (value) {
    const positionCookieName = value.indexOf(cookieName) - 1;
    const lastPositionCookieValue = value
      .substring(positionCookieName)
      .indexOf(':');

    return value.substring(
      positionCookieName,
      positionCookieName + lastPositionCookieValue
    );
  }
  return null;
};

export const getHeliosSupportedLanguage = (selectedLanguage: LanguageTypes) => {
  return HELIOS_SUPPORTED_LANGUAGES[selectedLanguage?.seoToken]
    ? HELIOS_SUPPORTED_LANGUAGES[selectedLanguage?.seoToken]
    : HELIOS_SUPPORTED_LANGUAGES['en'];
};

export const formatNumberToCurrency = ({
  number = 0,
  currency = 'USD',
  locale = 'en_US'
}) => {
  // The locales sent by WCS differ from the ones used in Javascript. We are
  // replacing '_' with '-' for correct format.

  const formattedLocal = locale.replace('_', '-');

  const numberWithCurrecnySymbol = new Intl.NumberFormat(formattedLocal, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol'
  }).format(number);

  return `${numberWithCurrecnySymbol || 0}`;
};

export const getEstimatedFulfillmentLabel = (
  fulfillment: Fulfillment
): string => {
  if (fulfillment?.delivery) {
    return 'ESTIMATED_DELIVERY_DATE';
  } else if (fulfillment?.dropBox) {
    return 'ESTIMATED_FULFILLMENT_DATE';
  }
  return 'ESTIMATED_FULFILLMENT_PICKUP_DATE';
};

export const getDate = (
  locale: string,
  dealerDateFormat: string,
  featureReleaseDate: string
) => {
  try {
    const date = parse(featureReleaseDate, 'MM/dd/yyyy', new Date());
    return format(date, dealerDateFormat, {
      locale: locale ? DATE_FNS_LOCALES[locale] : DATE_FNS_LOCALES.en_US
    });
  } catch (e) {
    return featureReleaseDate;
  }
};
